import { useState } from "react";
import { type Standing, api } from "../lib/api.ts";
/**
 * What an unaffiliated person sees: either the form that registers their team,
 * or the holding screen while we review it. Nothing else is reachable until a
 * team is approved, so this screen carries the whole state on its own.
 */
import { Button, Card, InfoBanner, Input, Textarea } from "../ui/index.ts";

export function Register({
	standing,
	onRegistered,
}: {
	standing: Standing;
	onRegistered: () => void;
}) {
	const [name, setName] = useState("");
	const [note, setNote] = useState("");
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);

	if (standing.standing === "pending") {
		const team = standing.team;
		const rejected = team?.status === "rejected";
		const suspended = team?.status === "suspended";
		return (
			<main className="grid min-h-dvh place-items-center p-6">
				<Card className="w-full max-w-md p-8">
					<p className="text-xs font-bold uppercase tracking-[0.16em] text-muted">
						{team?.name ?? "Your team"}
					</p>
					<h1 className="mt-3 text-2xl font-bold text-content">
						{rejected ? "Not approved." : suspended ? "On hold." : "With us for review."}
					</h1>
					<p className="mt-2 text-sm text-muted">
						{rejected
							? "This registration was turned down. Talk to your G1 contact if that looks wrong."
							: suspended
								? "Access to this team is paused. Your G1 contact can lift it."
								: "We check every team by hand before lending out ad accounts. You will get access to whatever we assign once that is done — nothing to do in the meantime."}
					</p>
				</Card>
			</main>
		);
	}

	async function submit(e: React.FormEvent) {
		e.preventDefault();
		setBusy(true);
		setError(null);
		try {
			await api.registerTeam({ name: name.trim(), note: note.trim() || undefined });
			onRegistered();
		} catch (err) {
			setError(err instanceof Error ? err.message : String(err));
		} finally {
			setBusy(false);
		}
	}

	return (
		<main className="grid min-h-dvh place-items-center p-6">
			<Card className="w-full max-w-md p-8">
				<p className="text-xs font-bold uppercase tracking-[0.16em] text-muted">G1 Network</p>
				<h1 className="mt-3 text-2xl font-bold text-content">Register your team</h1>
				<p className="mt-2 text-sm text-muted">
					Tell us who you are. Once we approve the team you will see the ad accounts, proxies and
					agents we assign to it — you never handle the credentials behind them.
				</p>
				{error && (
					<div className="mt-4">
						<InfoBanner variant="danger" title={error} />
					</div>
				)}
				<form className="mt-6 space-y-4" onSubmit={submit}>
					<Input
						label="Team name"
						value={name}
						onChange={(e) => setName(e.target.value)}
						className="w-full"
					/>
					<Textarea
						label="About the team"
						rows={3}
						placeholder="What you plan to run, and who referred you (optional)"
						value={note}
						onChange={(e) => setNote(e.target.value)}
						className="w-full"
					/>
					<Button type="submit" loading={busy} disabled={name.trim().length < 2}>
						Send for review
					</Button>
				</form>
				<p className="mt-4 text-xs text-muted">
					You are registering as the team's owner. You can add your own people once approved.
				</p>
			</Card>
		</main>
	);
}
